import { useEffect } from "react";
import { UnisphereElement, UnisphereElementProps, useGetUnisphereWorkspace } from "@unisphere/runtime-react";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { UnisphereElementBaseType } from "@unisphere/runtime";

interface BaseRuntimeType {
    widgetName: string;
    runtimeName: string;
}

export interface UnisphereWidgetProps<T extends BaseRuntimeType>
    extends Omit<UnisphereElementProps, "widgetName" | "runtimeName">,
        Pick<T, "widgetName" | "runtimeName"> {
    onLoad?: (widget: T & UnisphereElementBaseType) => void;
}

/**
 * Component to render unisphere widget.
 * It uses the `forwardRef` and `useImperativeHandle` hooks to expose a method `getWidget`
 * that allows parent components to access the widget instance and invoke public methods on it.
 */
export const UnisphereWidget = <T extends BaseRuntimeType>({ onLoad, ...props }: UnisphereWidgetProps<T>) => {
    const { widgetName, runtimeName } = props;
    const { unisphereWorkspace } = useGetUnisphereWorkspace();

    const handleLoad = useEventHandler(onLoad);
    useEffect(() => {
        unisphereWorkspace?.onRuntimeLoaded<T>(widgetName, runtimeName, ({ runtime }) => handleLoad(runtime));
    }, [unisphereWorkspace, widgetName, runtimeName]);

    return <UnisphereElement {...props} />;
};
